import { useApps } from '@auth/useApps'
import {
  CursorResult,
  listTimelineItems,
  TimelineItem,
  TimelineItemsParams,
} from '@kudos/http-client'
import { AppPermission } from '@shared/permissions'
import { firstValue, SearchState } from '@shared/search/state'
import { TimelineItemTypeFilterField } from '@shared/timeline/types'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { awardEmbeds, recognitionEmbeds } from '@tenant/home/user/timeline/useTimelineItems'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/shallow'
import { useMyTimelineFiltersStore } from './useMyTimelineFiltersStore'

export const MY_TIMELINE_ITEMS_QK = 'myTimelineItemsQueryKey'

export const useMyTimelineItems = () => {
  const [hasAppPermission] = useApps()
  const canBrowseRecognitions = hasAppPermission(
    'recognitionApp',
    AppPermission.RecognitionAppBrowse,
  )
  const queryClient = useQueryClient()
  const { myTimelineFilters, setMyTimelineFilters } = useMyTimelineFiltersStore(
    useShallow(state => ({
      myTimelineFilters: state.myTimelineFilters,
      setMyTimelineFilters: state.setMyTimelineFilters,
    })),
  )
  const [filters, setFilters] =
    useState<SearchState<TimelineItemTypeFilterField>>(myTimelineFilters)

  const parseMyTimelineFilters = (
    filters: SearchState<TimelineItemTypeFilterField>,
  ): TimelineItemsParams => {
    const itemTypeIn =
      filters.itemTypeIn.length === 0 ? ['award', 'recognition'] : filters.itemTypeIn

    return { itemTypeIn, my: true, includePinned: true }
  }

  useEffect(() => {
    if (!canBrowseRecognitions && firstValue(myTimelineFilters.itemTypeIn) === 'recognition') {
      setFilters({
        itemTypeIn: [],
      })
    }
  }, [canBrowseRecognitions, myTimelineFilters])

  const resetInfiniteQueryPagination = (): void => {
    queryClient.setQueryData(
      [MY_TIMELINE_ITEMS_QK],
      (oldData: {
        pageParams: { pageParam: null | string }[]
        pages: CursorResult<TimelineItem>[]
      }) => {
        if (!oldData) return undefined

        return {
          pages: oldData.pages.slice(0, 1),
          pageParams: oldData.pageParams.slice(0, 1),
        }
      },
    )
  }

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, isFetching } =
    useInfiniteQuery({
      queryKey: [MY_TIMELINE_ITEMS_QK],
      queryFn: ({ pageParam = null }: { pageParam: null | string }) =>
        listTimelineItems({
          cursor: pageParam,
          recognitionEmbeds: [...recognitionEmbeds, 'ownRecipient'],
          awardEmbeds: [...awardEmbeds, 'ownRecipient'],
          ...parseMyTimelineFilters(filters),
        }),
      staleTime: 1 * 60 * 1000,
      getNextPageParam: lastPage => lastPage.next,
      initialPageParam: null,
      refetchOnMount: false,
    })

  useEffect(() => {
    setMyTimelineFilters(filters)
    resetInfiniteQueryPagination()
    refetch()
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.itemTypeIn])

  return {
    timelineData: data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    filters,
    setFilters,
    isLoading: isLoading || isFetching,
    canBrowseRecognitions,
  }
}
